import { Card, Fade, Typography, keyframes, useTheme } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { sleep } from '@/util/time';

type FadeSlideProps = {
  duration?: number;
  message: string;
  onDone: () => void;
};

const ANIMATION_DURATION = 500;
const DEFAULT_DURATION = 500;

type AnimationDirection = 'in' | 'out';

const getFadeSlideKeyframes = (direction: AnimationDirection) => {
  return keyframes`
    from {
      opacity: ${direction === 'in' ? 0 : 1};
      transform: translateY(${direction === 'in' ? '100%' : '0'});
    }
    to {
      opacity: ${direction === 'in' ? 1 : 0};
      transform: translateY(${direction === 'in' ? '0' : '-100%'});
    }
  `;
};

const FadeSlide: FC<FadeSlideProps> = ({ duration = DEFAULT_DURATION, message, onDone }) => {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    setChecked(true);

    sleep(duration)
      .then(() => {
        setChecked(false);
        return sleep(ANIMATION_DURATION);
      })
      .then(() => {
        onDone();
      });
  }, [duration, onDone]);

  const theme = useTheme();

  const bgColor = theme.palette.mode === 'light' ? theme.palette.grey[700] : theme.palette.grey[300];

  return (
    <Fade in={checked} timeout={duration}>
      <Card
        sx={{
          p: 2,
          m: 2,
          bgcolor: bgColor,
          color: theme.palette.getContrastText(bgColor),
          animationName: `${getFadeSlideKeyframes(checked ? 'in' : 'out')}`,
          animationDuration: `${ANIMATION_DURATION}ms`,
          animationFillMode: 'forwards'
        }}
      >
        <Typography variant='subtitle1' fontWeight={700}>
          {message}
        </Typography>
      </Card>
    </Fade>
  );
};

export default FadeSlide;
